import React, {useEffect, useState} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Pagination} from 'swiper/modules';
import type {Swiper as SwiperType} from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import {getPopupsBySection, Popup} from '../../api/commonApi';

interface PopupSwiperProps {
    section: string;
}

// 팝업 코드 기준 오늘 하루 닫기 키 (팝업별 개별 관리)
const getHideTodayKey = (popupCode: string) => {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    return `popup_hide_${popupCode}_${today}`;
};

export default function PopupSwiper({section}: PopupSwiperProps) {
    const [popups, setPopups] = useState<Popup[]>([]);
    const [visible, setVisible] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const fetchPopups = async () => {
            try {
                const response = await getPopupsBySection(section);
                if (response.data.code === '00' && response.data.data.length > 0) {
                    // 오늘 하루 닫기를 누른 팝업은 팝업 코드 기준으로 개별 제외
                    const active = response.data.data.filter(
                        p => !localStorage.getItem(getHideTodayKey(p.popupCode))
                    );
                    if (active.length > 0) {
                        setPopups(active);
                        setVisible(true);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch popups:', error);
            }
        };

        fetchPopups();
    }, [section]);

    const handleClose = () => setVisible(false);

    // 해당 팝업만 오늘 하루 숨김, 남은 팝업 없으면 모달 닫기
    const handleHideToday = (popupCode: string) => {
        localStorage.setItem(getHideTodayKey(popupCode), '1');
        setPopups(prev => {
            const updated = prev.filter(p => p.popupCode !== popupCode);
            if (updated.length === 0) setVisible(false);
            // 활성 인덱스 보정 (삭제 후 범위 초과 방지)
            setActiveIndex(idx => Math.min(idx, Math.max(0, updated.length - 1)));
            return updated;
        });
    };

    const handleLinkClick = (popup: Popup) => {
        if (!popup.linkUrl) return;
        if (popup.target === '_blank') {
            window.open(popup.linkUrl, '_blank', 'noopener,noreferrer');
        } else {
            window.location.href = popup.linkUrl;
        }
    };

    if (!visible || popups.length === 0) return null;

    // 현재 슬라이드 팝업 기준으로 버튼 렌더링
    const currentPopup = popups[activeIndex] ?? popups[0];

    return (
        <div id="popUp">
            <div className="popup">
                <div className="popup-banner">
                    <Swiper
                        modules={[Pagination]}
                        pagination={popups.length > 1 ? {clickable: true} : false}
                        slidesPerView={1}
                        spaceBetween={0}
                        onSlideChange={(swiper: SwiperType) => setActiveIndex(swiper.activeIndex)}
                    >
                        {popups.map(popup => (
                            <SwiperSlide key={popup.popupCode}>
                                <a
                                    onClick={() => handleLinkClick(popup)}
                                    style={{cursor: popup.linkUrl ? 'pointer' : 'default'}}
                                >
                                    {popup.popupImageUrl && (
                                        <img
                                            src={popup.popupImageUrl}
                                            alt={popup.popupName}
                                            onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                            }}
                                        />
                                    )}
                                </a>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    {/* 현재 슬라이드 팝업의 hideTodayEnabled 기준으로 버튼 렌더링 */}
                    <div className="btn-group">
                        <ul>
                            {currentPopup.hideTodayEnabled && (
                                <li>
                                    <button type="button" onClick={() => handleHideToday(currentPopup.popupCode)}>
                                        오늘 하루 닫기
                                    </button>
                                </li>
                            )}
                            <li style={!currentPopup.hideTodayEnabled ? {width: '100%'} : undefined}>
                                <button type="button" onClick={handleClose}>닫기</button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div className="mask" onClick={handleClose}></div>
        </div>
    );
}
